import { useState, useMemo } from "react";
import { Link } from "@/lib/router-compat";
import { Search } from "lucide-react";
import { blogImages } from "@/assets/blog";
import Layout from "@/components/Layout";
import PageSEO from "@/components/PageSEO";
import PageHero from "@/components/PageHero";
import FinalContactSection from "@/components/FinalContactSection";
import { Button } from "@/components/ui/button";
import { blogPosts, blogCategories, type BlogCategory } from "@/data/blog-posts";
import { COMPANY, breadcrumbSchema } from "@/lib/seo";

const headingId = (title: string) => `article-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;

const fallbackImages: Record<string, string> = {
  HVAC: blogImages["commercial-hvac-maintenance-louisville-ky"]!,
  "Energy Management": blogImages["energy-audit-louisville-ky"]!,
  "Electrical Safety": blogImages["emergency-electrician-louisville-southern-indiana"]!,
  "Commercial Electrical": blogImages["commercial-electrician-louisville-ky"]!,
  "Residential Electrical": blogImages["residential-electrical-services-louisville-ky"]!,
  "Smart Building & EV": blogImages["ev-charger-installation-louisville-ky"]!,
};

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState<BlogCategory>("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesCategory = activeCategory === "All" || post.category === activeCategory;
      const matchesSearch = !searchQuery || 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const schemas = [
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Blog", url: "/blog" },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Blog & Resources",
      description: "Expert advice on electrical safety, energy savings, and smart building solutions for Louisville, KY and Southern Indiana businesses and homeowners.",
      url: `${COMPANY.url}/blog`,
      inLanguage: "en-US",
      publisher: {
        "@type": "Organization",
        name: COMPANY.name,
        logo: { "@type": "ImageObject", url: `${COMPANY.url}/images/hadley-logo.png`, width: 1428, height: 883 },
      },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: blogPosts.map((post, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: post.title,
          url: `${COMPANY.url}/blog/${post.slug}`,
        })),
      },
    },
  ];

  return (
    <Layout>
      <PageSEO
        title="Blog | Hadley Energy Solutions"
        description="Expert advice on electrical safety, energy savings & smart building solutions for Louisville, KY businesses and homeowners."
        canonical="/blog"
        schemas={schemas}
      />
      <PageHero
        title="Insights & Resources"
        subtitle="Expert advice on electrical safety, energy savings, and smart building solutions for Louisville, KY and Southern Indiana businesses and homeowners."
        eyebrow="INSIGHTS"
        breadcrumbs={[{ label: "Blog" }]}
      />

      <section className="section-padding">
        <div className="container">
          {/* Filters */}
          <div className="mb-10 flex flex-col gap-5 border-b border-border pb-5 md:flex-row md:items-end">
            {/* Category Filter */}
            <div className="flex flex-1 flex-wrap gap-x-6 gap-y-2">
              {blogCategories.map(cat => (
                <Button
                  key={cat}
                  variant="ghost"
                  onClick={() => setActiveCategory(cat)}
                  className={`h-auto rounded-none border-b-2 px-0 py-2 font-condensed text-[13px] font-bold uppercase transition-colors ${
                    activeCategory === cat
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:bg-transparent hover:text-foreground"
                  }`}
                >
                  {cat}
                </Button>
              ))}
            </div>
            {/* Search */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full rounded-[4px] border border-border bg-background py-2.5 pl-10 pr-4 font-body text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          {/* Blog Grid */}
          {filteredPosts.length > 0 ? (
            <div className="grid gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map((post, i) => (
                <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group block overflow-hidden rounded-[4px] border border-border bg-card transition-transform duration-200 hover:-translate-y-0.5"
                  >
                    <div className="aspect-video overflow-hidden bg-muted">
                      <img src={blogImages[post.slug] || fallbackImages[post.category]} alt={post.heroImageAlt} className="h-full w-full object-cover" loading={i < 2 ? "eager" : "lazy"} decoding="async" fetchPriority="low" width="800" height="450" />
                    </div>
                    <div className="p-5">
                      <div className="mb-3 flex items-center justify-between gap-3 font-condensed text-[13px] font-bold uppercase">
                        <span className="text-primary">{post.category}</span>
                        <span className="text-muted-foreground">{post.readTime}</span>
                      </div>
                      <h2 id={headingId(post.title)} className="mb-2 font-heading text-[20px] font-bold leading-snug group-hover:text-primary">
                        {post.title}
                      </h2>
                      <p className="mb-4 line-clamp-3 font-body text-sm leading-relaxed text-muted-foreground">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between border-t border-border pt-3">
                        <time dateTime={post.publishedDate} className="font-body text-xs text-muted-foreground">
                          {new Date(post.publishedDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                        </time>
                        <span className="font-condensed text-[13px] font-bold uppercase text-primary">Read More →</span>
                      </div>
                    </div>
                  </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground font-body text-lg">No articles found matching your criteria.</p>
            </div>
          )}

          <div className="mt-14 border-l-2 border-primary pl-5">
            <h3 className="font-heading text-2xl font-bold">Schedule Your Free Energy Audit</h3>
            <p className="mt-2 max-w-2xl font-body text-muted-foreground">Let our certified energy auditors identify where your building is wasting money — and how to fix it.</p>
          </div>
        </div>
      </section>

      <FinalContactSection source="blog_index_form" />
    </Layout>
  );
};

export default BlogPage;
